import { defineStore } from 'pinia'
import { auth, db } from '@/firebase'
import { Invoice, InvoiceAction } from '@/types/invoice'
import { collection, doc, getCountFromServer, getDoc, getDocs, limit, orderBy, query, setDoc, where, deleteDoc } from 'firebase/firestore'
import { useFilesStore } from '@/store/filestore'
import { useAuthStore } from '@/store/authstore'
import { useExpenseStore } from '@/store/expensestore'
import { toMonthKey } from '@/utils/format'
import { FREE_TIER_LIMIT } from '@/utils/freeTier'

export const useInvoiceStore = defineStore('invoiceStore', {
    state: () => ({
        invoices: [] as Invoice[],
        invoicesByMonth: {} as Record<string, Invoice[]>,
        currentMonths: [] as string[],
        allInvoicesFetched: false,
        invoiceCount: null as number | null,
        recentInvoices: [] as Invoice[],
        selectedInvoice: null as Invoice | null,
        selectedInvoiceAction: null as InvoiceAction | null,
        error: null as string | null,
        loading: false
    }),

    actions: {
        // Patches the byMonth cache in place with a document we just wrote to Firestore,
        // instead of invalidating the whole cache and forcing a full collection re-read.
        _patchInvoiceCache(invoice: Invoice, previousId?: string) {
            const staleId = previousId ?? invoice.id;
            for (const key of Object.keys(this.invoicesByMonth)) {
                this.invoicesByMonth[key] = this.invoicesByMonth[key].filter((i) => i.id !== staleId);
            }

            const key = toMonthKey(invoice.invoiceDate);
            if (key && (this.invoicesByMonth[key] || this.allInvoicesFetched)) {
                this.invoicesByMonth[key] = [...(this.invoicesByMonth[key] || []), invoice];
            }

            this.invoices = this.currentMonths.flatMap((m) => this.invoicesByMonth[m] || []);
        },

        _removeInvoiceFromCache(id: string) {
            for (const key of Object.keys(this.invoicesByMonth)) {
                this.invoicesByMonth[key] = this.invoicesByMonth[key].filter((i) => i.id !== id);
            }
            this.invoices = this.currentMonths.flatMap((m) => this.invoicesByMonth[m] || []);
        },

        async fetchInvoiceCount() {
            if (this.invoiceCount !== null) return;
            try {
                const user = auth.currentUser;
                if (!user) return;
                const invoicesRef = collection(db, `users/${user.uid}/invoices`);
                const snap = await getCountFromServer(invoicesRef);
                this.invoiceCount = snap.data().count;
            } catch (err) {
                console.error(err);
            }
        },

        async addInvoice(invoice: Omit<Invoice, 'id'>) {
            this.error = null;
            try {

                const user = auth.currentUser;
                if (!user) throw new Error('Bruger er ikke logget ind');
                if (!invoice.invoiceNumber) throw new Error('Fakturanummeret er ikke udfyldt');

                const authStore = useAuthStore();
                if (!authStore.user?.hasAccess) {
                    const expenseStore = useExpenseStore();
                    await Promise.all([this.fetchInvoiceCount(), expenseStore.fetchExpenseCount()]);
                    const total = (this.invoiceCount ?? 0) + (expenseStore.expenseCount ?? 0);
                    if (total >= FREE_TIER_LIMIT) {
                        throw new Error(`Du har nået den gratis grænse på ${FREE_TIER_LIMIT} fakturaer og køb. Opgrader for at oprette flere.`);
                    }
                }

                const invoicesRef = collection(db, `users/${user.uid}/invoices`);
                const docRef = doc(invoicesRef, invoice.invoiceNumber);

                const existingDoc = await getDoc(docRef);
                if (existingDoc.exists()) {
                    throw new Error(`Fakturanummeret "${invoice.invoiceNumber}" findes allerede.`);
                }

                await setDoc(docRef, invoice);
                this._patchInvoiceCache({ id: invoice.invoiceNumber, ...invoice });
                if (this.invoiceCount !== null) this.invoiceCount += 1;
            } catch (err: any) {
                this.error = err.message || 'Kunne ikke tilføje faktura';
                console.error(err);
            }
        },

        async updateInvoice(invoice: Invoice) {
            this.error = null;
            try {
                const user = auth.currentUser;
                if (!user) throw new Error('Bruger er ikke logget ind');
                if (!invoice.invoiceNumber) throw new Error('Fakturanummeret er ikke udfyldt');

                const invoicesRef = collection(db, `users/${user.uid}/invoices`);

                const { id, ...invoiceData } = invoice;

                if (id && id !== invoice.invoiceNumber) {
                    const newDocRef = doc(invoicesRef, invoice.invoiceNumber);
                    const existingDoc = await getDoc(newDocRef);
                    if (existingDoc.exists()) {
                        throw new Error(`Fakturanummeret "${invoice.invoiceNumber}" findes allerede.`);
                    }
                    await deleteDoc(doc(invoicesRef, id));
                    await setDoc(newDocRef, invoiceData);
                } else {
                    const docRef = doc(invoicesRef, invoice.invoiceNumber);
                    await setDoc(docRef, invoiceData);
                }
                const previousId = id && id !== invoice.invoiceNumber ? id : undefined;
                this._patchInvoiceCache({ id: invoice.invoiceNumber, ...invoiceData }, previousId);
            } catch (err: any) {
                this.error = err.message || 'Kunne ikke opdatere faktura';
                console.error(err);
            }
        },

        async fetchInvoices(months: string[] = []) {
            this.error = null;
            this.loading = true;

            try {
                const user = auth.currentUser;
                if (!user) throw new Error("Bruger er ikke logget ind");

                if (months.length === 0) {
                    const now = new Date();
                    const currentYear = now.getFullYear();
                    const currentMonth = String(now.getMonth() + 1).padStart(2, "0");
                    months = [`${currentYear}-${currentMonth}`];
                }

                const invoicesRef = collection(db, `users/${user.uid}/invoices`);

                for (const monthStr of months) {
                    if (this.invoicesByMonth[monthStr] || this.allInvoicesFetched) continue;

                    const [year, month] = monthStr.split("-");
                    const start = `${year}-${month}-01`;
                    const endDate = new Date(parseInt(year), parseInt(month), 0);
                    const end = `${year}-${month}-${String(endDate.getDate()).padStart(2, "0")}`;

                    const q = query(
                        invoicesRef,
                        where("invoiceDate", ">=", start),
                        where("invoiceDate", "<=", end)
                    );

                    const monthSnap = await getDocs(q);
                    this.invoicesByMonth[monthStr] = monthSnap.docs.map((d) => {
                        const { id: _stored, ...data } = d.data();
                        return { id: d.id, ...(data as Omit<Invoice, "id">) };
                    });
                }

                this.currentMonths = months;
                this.invoices = months.flatMap((m) => this.invoicesByMonth[m] || []);
            } catch (err: any) {
                this.error = "Kunne ikke hente fakturaer";
                console.error(err);
            } finally {
                this.loading = false;
            }
        },


        async fetchAllInvoices() {
            if (this.allInvoicesFetched) return;

            this.error = null;
            this.loading = true;
            try {
                const user = auth.currentUser;
                if (!user) throw new Error("Bruger er ikke logget ind");

                const invoicesRef = collection(db, `users/${user.uid}/invoices`);
                const snap = await getDocs(invoicesRef);
                this.invoices = snap.docs.map((d) => {
                    const { id: _stored, ...data } = d.data();
                    return { id: d.id, ...(data as Omit<Invoice, "id">) };
                });

                const byMonth: Record<string, Invoice[]> = {};
                for (const invoice of this.invoices) {
                    const key = toMonthKey(invoice.invoiceDate);
                    if (!key) continue;
                    (byMonth[key] ??= []).push(invoice);
                }
                this.invoicesByMonth = byMonth;
                this.allInvoicesFetched = true;
                this.currentMonths = Object.keys(byMonth);
            } catch (err: any) {
                this.error = "Kunne ikke hente fakturaer";
                console.error(err);
            } finally {
                this.loading = false;
            }
        },

        async fetchRecentInvoices(count = 10) {
            try {
                const user = auth.currentUser;
                if (!user) throw new Error("Bruger er ikke logget ind");

                const invoicesRef = collection(db, `users/${user.uid}/invoices`);
                const q = query(invoicesRef, orderBy("invoiceDate", "desc"), limit(count));
                const snap = await getDocs(q);
                this.recentInvoices = snap.docs.map((d) => {
                    const { id: _stored, ...data } = d.data();
                    return { id: d.id, ...(data as Omit<Invoice, "id">) };
                });
            } catch (err: any) {
                console.error(err);
            }
        },

        async fetchInvoice(invoiceNumber: string) {
            this.error = null
            this.loading = true
            try {
                const user = auth.currentUser
                if (!user) throw new Error('Bruger er ikke logget ind')

                const invoicesRef = collection(db, `users/${user.uid}/invoices`)
                const q = query(invoicesRef, where('invoiceNumber', '==', invoiceNumber), limit(1))
                const snapshot = await getDocs(q)

                if (snapshot.empty) {
                    this.error = 'Faktura ikke fundet'
                    return null
                }

                const docSnap = snapshot.docs[0];
                const { id: _stored, ...data } = docSnap.data();
                return { id: docSnap.id, ...(data as Omit<Invoice, 'id'>) };
            } catch (err: any) {
                this.error = 'Kunne ikke hente faktura'
                console.error(err)
                return null
            } finally {
                this.loading = false
            }
        },

        async downloadPdf(invoiceElement: any, fileName?: string) {
            if (!invoiceElement.invoiceRef) {
                console.warn("invoiceRef is not ready");
                return;
            }

            const options = {

                filename: `${fileName || "invoice"}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            };

            const { default: html2pdf } = await import('html2pdf.js')
            html2pdf().set(options).from(invoiceElement.invoiceRef).save();
        },

        async downloadPdfPro(invoice: Invoice) {
            try {
                const { downloadInvoicePdf } = await import('@/services/pdf/invoicePdf')
                downloadInvoicePdf(invoice, `faktura-${invoice.invoiceNumber || 'uden-nummer'}.pdf`)
            } catch (e) {
                console.error('Failed to generate PDF with pdfmake', e)
            }
        },

        async deleteInvoice(invoice: Invoice) {
            this.error = null
            try {
                const user = auth.currentUser
                if (!user) throw new Error('Bruger er ikke logget ind')
                if (!invoice.invoiceNumber) throw new Error('Fakturanummeret er ikke udfyldt')

                const invoicesRef = collection(db, `users/${user.uid}/invoices`)
                const docRef = doc(invoicesRef, invoice.invoiceNumber)
                await deleteDoc(docRef)

                const filesStore = useFilesStore()
                await filesStore.deleteInvoiceLogo(invoice.invoiceNumber).catch(() => { })

                this._removeInvoiceFromCache(invoice.id ?? invoice.invoiceNumber)
                if (this.invoiceCount !== null) this.invoiceCount = Math.max(0, this.invoiceCount - 1)
                if (this.selectedInvoice?.invoiceNumber === invoice.invoiceNumber) {
                    this.selectedInvoice = null
                }
                this.selectedInvoiceAction = null
            } catch (err: any) {
                this.error = err?.message || 'Kunne ikke slette faktura'
                console.error(err)
            }
        }
    },
})
